// Security utilities for Theo EduMarket Rwanda
// Prevents XSS, injection, and brute-force attacks

export function sanitizeInput(str: string): string {
  if (typeof str !== 'string') return ''
  return str
    .replace(/<[^>]*>/g, '')
    .replace(/[<>"'&]/g, (match) => {
      const map: Record<string, string> = {
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#x27;',
        '&': '&amp;',
      }
      return map[match] || match
    })
    .trim()
    .slice(0, 500)
}

export function sanitizeHtml(str: string): string {
  if (typeof str !== 'string') return ''
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
}

export function validatePhone(phone: string): boolean {
  return /^\d{10}$/.test(phone)
}

export function validateEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

export function validatePrice(price: string): boolean {
  const num = Number(price)
  return !isNaN(num) && num >= 0 && num <= 10000000
}

// Rate limiter for signup/login attempts
const rateLimitMap = new Map<string, { count: number; resetAt: number }>()

export function checkRateLimit(
  key: string,
  maxAttempts: number = 10,
  windowMs: number = 60000,
): { allowed: boolean; remainingAttempts: number; retryAfterMs?: number } {
  const now = Date.now()
  const entry = rateLimitMap.get(key)

  if (!entry || now >= entry.resetAt) {
    rateLimitMap.set(key, { count: 1, resetAt: now + windowMs })
    return { allowed: true, remainingAttempts: maxAttempts - 1 }
  }

  if (entry.count >= maxAttempts) {
    return {
      allowed: false,
      remainingAttempts: 0,
      retryAfterMs: entry.resetAt - now,
    }
  }

  entry.count++
  return { allowed: true, remainingAttempts: maxAttempts - entry.count }
}

// Generate a simple session token
export function generateSessionToken(): string {
  const arr = new Uint8Array(32)
  crypto.getRandomValues(arr)
  return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('')
}

// Verify session token format
export function isValidSessionToken(token: string): boolean {
  return typeof token === 'string' && /^[a-f0-9]{64}$/.test(token)
}

// Content Security - prevent localStorage tampering detection
export function createIntegrityHash(data: string): string {
  let hash = 0
  for (let i = 0; i < data.length; i++) {
    const char = data.charCodeAt(i)
    hash = ((hash << 5) - hash + char) | 0
  }
  return 'integrity_' + Math.abs(hash).toString(36)
}

export function verifyIntegrity(data: string, hash: string): boolean {
  return createIntegrityHash(data) === hash
}
